import { useState, useEffect, useRef } from "react";
import { Container, TextInput, Box, Stack, Text, Loader, Center, Title, Flex, Pagination, Badge } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch, IconSparkles } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

import CompanyCardHorizontal, { CompanyWithUI } from "../../components/shared/CompanyCardHorizontal";
import InfoBanner from "../../components/shared/InfoBanner";
// import MultiSelectWithBadge from "../discover/Multiselectwithbadge ";
// import {
//   countriesData,
//   getIndustryOptions,
//   OPERATING_INDUSTRY_LABELS,
// } from "../../components/shared/constants/general.const";
import trpc from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util.ts";
// import FilterChips from "./FilterChips.tsx";

const PAGE_SIZE = 10;

export default function DiscoverPage() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchQuery, 700);
  const latestAiQueryRef = useRef<string>("");
  // const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  // const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [companies, setCompanies] = useState<CompanyWithUI[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filterChanged, setFilterChanged] = useState(false);
  const [isAiSearch, setIsAiSearch] = useState(false);

  const MAX_RETRIES = 2;

  // Accepts explicit page to avoid stale closure when called after page reset
  const fetchCompanies = async (pageToFetch = page) => {
    if (retryCount >= MAX_RETRIES) return;

    setLoading(true);
    setLoadError(null);

    try {
      const response = await trpc.company.listAll.query({
        page: pageToFetch,
        limit: PAGE_SIZE,
      });

      const mappedCompanies: CompanyWithUI[] = (response.companies || []).map((item: any) => ({
        ...item,
        isShortlisted: false,
      })) as CompanyWithUI[];

      setCompanies(mappedCompanies);
      setTotalPages(response.pagination?.totalPages || 1);
      setRetryCount(0);
    } catch (err) {
      handleTrpcError(err);
      const nextRetry = retryCount + 1;
      setRetryCount(nextRetry);
      if (nextRetry >= MAX_RETRIES) {
        setLoadError("Unable to load companies. Please try again later.");
      }
    } finally {
      setLoading(false);
      setFilterChanged(false);
    }
  };

  const fetchAiSearch = async (query: string) => {
    latestAiQueryRef.current = query;
    setLoading(true);
    setLoadError(null);
    setIsAiSearch(true);

    try {
      const response = await trpc.company.searchAI.query({ query });

      if (latestAiQueryRef.current !== query) return;

      // Extract results from nested structure
      const results = response?.result?.data?.json?.results || response.results || response.companies || [];

      // Helper to ensure array fields are always arrays
      const ensureArray = (value: any): any[] => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        return [value];
      };

      const mappedCompanies: CompanyWithUI[] = results.map((item: any) => {
        const companyId = item.company_id || item.id;

        return {
          id: companyId,
          gtid: item.gtid,
          legalName: item.legal_name || item.legalName,
          tradeName: item.trade_name || item.tradeName,
          registrationNumber: item.registration_number || item.registrationNumber,
          yearOfIncorporation: item.year_of_incorporation || item.yearOfIncorporation,
          website: item.company_website || item.website,
          email: item.director_email || item.email,
          phoneCountryCode: item.phone_country_code || item.phoneCountryCode,
          phoneNumber: item.phone_number || item.phoneNumber,
          description: item.description,
          industryCategory: ensureArray(item.industry_category || item.industryCategory),
          subIndustry: ensureArray(item.sub_industry || item.subIndustry),
          collaborationIntent: ensureArray(item.collaboration_intent || item.collaborationIntent),
          coreCapabilities: ensureArray(item.core_capabilities || item.coreCapabilities),
          secondaryCapabilities: ensureArray(item.secondary_capabilities || item.secondaryCapabilities),
          keyDifferentiators: item.key_differentiators || item.keyDifferentiators,
          technologies: ensureArray(item.technologies_infrastructure || item.technologies),
          headquartersCountry: item.country || item.headquartersCountry,
          headquartersCity: item.state || item.headquartersCity,
          currentAddress: item.headquarters_location || item.currentAddress,
          operatingCountries: ensureArray(item.operating_countries || item.operatingCountries),
          preferredCollaborationRegions: ensureArray(item.preferred_collaboration_regions || item.preferredCollaborationRegions),
          status: item.status || "VERIFIED",
          isShortlisted: false,
        } as CompanyWithUI;
      });

      setCompanies(mappedCompanies);
      setTotalPages(1);
    } catch (err) {
      if (latestAiQueryRef.current !== query) return;
      handleTrpcError(err);
      setLoadError("AI search failed. Please try again.");
    } finally {
      if (latestAiQueryRef.current === query) {
        setLoading(false);
      }
    }
  };

  // Triggers on search query change. Resets page and dispatches the right fetch.
  useEffect(() => {
    setPage(1);
    setFilterChanged(true);
    setCompanies([]);
    setRetryCount(0);
    setLoadError(null);

    if (debouncedSearch) {
      fetchAiSearch(debouncedSearch);
      setFilterChanged(false);
      return;
    }

    setIsAiSearch(false);
    fetchCompanies(1);
  }, [debouncedSearch]);

  // Handles user-initiated page changes. Skipped when a search change already fetched.
  useEffect(() => {
    if (filterChanged) return;
    if (isAiSearch) return;

    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchCompanies();
  }, [page]);

  return (
    <Box mih="100vh" pb={40}>
      {/* Mobile Header */}
      <Box py="md" hiddenFrom="sm">
        <Container size="lg">
          <Title order={2} fz={20} fw={600}>
            Discover
          </Title>
          <Text size="sm" c="slate.7">
            Discover verified companies with advanced search criteria.
          </Text>
        </Container>
      </Box>

      <Container size="lg" py={{ base: 0, sm: 40 }}>
        <TextInput
          placeholder="Search by capability, industry, or keyword..."
          leftSection={<IconSearch size={18} />}
          size="md"
          radius="lg"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          mb="lg"
        />

        {/* Filters hidden — uncomment to restore
        <Group gap="md" align="flex-end" visibleFrom="sm">
          <MultiSelectWithBadge placeholder="Industry" data={industryOptions} value={selectedIndustries} onChange={setSelectedIndustries} leftSection={<IconBuilding size={18} />} width={148} searchable clearable />
          <MultiSelectWithBadge placeholder="Country" data={countryOptions} value={selectedCountries} onChange={setSelectedCountries} leftSection={<IconMapPin size={18} />} width={148} searchable clearable />
        </Group>
        <Box hiddenFrom="sm">
          <Group grow gap="sm">
            <MultiSelectWithBadge placeholder="Industry" data={industryOptions} value={selectedIndustries} onChange={setSelectedIndustries} leftSection={<IconBuilding size={18} />} searchable clearable />
            <MultiSelectWithBadge placeholder="Country" data={countryOptions} value={selectedCountries} onChange={setSelectedCountries} leftSection={<IconMapPin size={18} />} searchable clearable />
          </Group>
        </Box>
        <Flex mt="lg" mb="lg">
          <FilterChips value={activeFilters} labelMap={filterLabelMap} onRemove={handleRemoveFilter} />
        </Flex>
        */}

        <InfoBanner
          icon={IconSparkles}
          title="Sign up to unlock more"
          subtitle="Create an account to express interest, shortlist companies, and access full profiles."
        />

        <Flex align="center" gap="sm" mt="lg" mb="lg">
          <Text size="md" fw={600}>
            {companies.length} companies found
          </Text>
          {isAiSearch && (
            <Badge leftSection={<IconSparkles size={12} />} color="violet" variant="light" size="sm">
              AI Search
            </Badge>
          )}
        </Flex>

        {companies.length === 0 && !loading ? (
          <Center py="xl">
            <Text c="slate.5">No companies found</Text>
          </Center>
        ) : (
          <Stack gap="md">
            {companies.map((company) => (
              <CompanyCardHorizontal
                key={company.id}
                company={company}
                onShortlistClick={(id, isShortlisted) =>
                  setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, isShortlisted } : c)))
                }
                onViewDetails={(id) => navigate(`/discover/${id}`)}
              />
            ))}
          </Stack>
        )}

        {loading && (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        )}

        {loadError && (
          <Center py="md">
            <Text c="red" size="sm">
              {loadError}
            </Text>
          </Center>
        )}

        {totalPages > 1 && !isAiSearch && (
          <Center mt="xl">
            <Pagination total={totalPages} value={page} onChange={setPage} size="md" radius="md" />
          </Center>
        )}
      </Container>
    </Box>
  );
}
